/** Enhances Shopify's injected banner without changing how consent is stored. */
class CookieConsent {
  constructor() {
    this.locked = false;
    this.banner = null;
    this.frame = null;
    this.widget = document.getElementById('cookie-consent-widget');
    this.widget?.querySelector('button').addEventListener('click', () => this.openPreferences());
    // The banner API can finish loading after the theme's deferred scripts.
    document.addEventListener('load', (event) => {
      if (event.target.tagName === 'SCRIPT') this.update();
    }, true);
    this.observer = new MutationObserver(() => {
      if (this.frame) return;
      this.frame = requestAnimationFrame(() => {
        this.frame = null;
        this.update();
      });
    });
    this.observer.observe(document.body, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['style', 'class', 'hidden', 'aria-hidden', 'open'],
    });
    document.addEventListener('keydown', (event) => this.onKeydown(event));
    this.update();
  }

  isVisible(element) {
    return element && !element.closest('[hidden], [aria-hidden="true"]') &&
      getComputedStyle(element).visibility !== 'hidden' && element.getClientRects().length > 0;
  }

  async openPreferences() {
    const button = this.widget.querySelector('button');
    const error = this.widget.querySelector('[role="status"]');
    button.disabled = true;
    error.hidden = true;
    this.preferenceTrigger = button;
    try {
      await window.privacyBanner.showPreferences();
      this.update();
      this.previousFocus = button;
    } catch {
      this.preferenceTrigger = null;
      error.hidden = false;
    } finally {
      button.disabled = false;
    }
  }

  /** Keep the page stationary on mobile too, then restore its exact scroll position. */
  update() {
    const banner = document.getElementById('shopify-pc__banner');
    const preferences = document.getElementById('shopify-pc__prefs');
    const visibleBanner = this.isVisible(banner) ? banner : null;
    const active = visibleBanner || this.isVisible(preferences);

    const settingsTitle = this.widget?.dataset.settingsTitle;
    if (settingsTitle) {
      ['shopify-pc__banner__body-title', 'shopify-pc__prefs__header-title'].forEach((id) => {
        const heading = document.getElementById(id);
        if (heading && heading.textContent !== settingsTitle) heading.textContent = settingsTitle;
      });
    }

    if (this.widget) {
      const hideWidget = Boolean(active) || typeof window.privacyBanner?.showPreferences !== 'function';
      if (this.widget.hidden !== hideWidget) this.widget.hidden = hideWidget;
    }

    if (this.isVisible(preferences)) {
      ['essential', 'preferences', 'marketing', 'analytics'].forEach((category) => {
        const option = preferences.querySelector(`#shopify-pc__prefs__${category}`);
        const template = document.getElementById(`cookie-consent-details-${category}`);
        if (option && template && !option.querySelector('.cookie-consent-details')) {
          option.append(template.content.cloneNode(true));
        }
      });
    }

    const legalNotice = document.getElementById('cookie-consent-legal-notice');
    if (visibleBanner && legalNotice && !visibleBanner.querySelector('.cookie-consent-legal-notice')) {
      visibleBanner.append(legalNotice.content.cloneNode(true));
    }

    if (active && !this.locked) {
      this.scrollX = window.scrollX;
      this.scrollY = window.scrollY;
      this.previousFocus = this.preferenceTrigger || document.activeElement;
      document.body.style.setProperty('--cookie-consent-scroll-top', `-${this.scrollY}px`);
      document.documentElement.classList.add('cookie-consent-open');
      this.locked = true;
    } else if (!active && this.locked) {
      document.documentElement.classList.remove('cookie-consent-open');
      document.body.style.removeProperty('--cookie-consent-scroll-top');
      window.scrollTo({ left: this.scrollX, top: this.scrollY, behavior: 'instant' });
      this.locked = false;
      if (this.previousFocus?.isConnected) this.previousFocus.focus({ preventScroll: true });
      this.preferenceTrigger = null;
    }

    if (!visibleBanner) this.focusedBanner = null;
    if (visibleBanner && !this.isVisible(preferences) && this.focusedBanner !== visibleBanner) {
      const accept = visibleBanner.querySelector('#shopify-pc__banner__btn-accept');
      if (accept) {
        accept.focus({ preventScroll: true });
        this.focusedBanner = visibleBanner;
      }
    }
    this.banner = visibleBanner;
  }

  onKeydown(event) {
    if (event.key !== 'Tab' || !this.banner || this.isVisible(document.getElementById('shopify-pc__prefs'))) return;
    const controls = Array.from(this.banner.querySelectorAll('a[href], button, [tabindex="0"]'))
      .filter((element) => !element.disabled && this.isVisible(element));
    if (!controls.length) return;
    const first = controls[0];
    const last = controls[controls.length - 1];
    if (!this.banner.contains(document.activeElement) ||
      (event.shiftKey && document.activeElement === first) ||
      (!event.shiftKey && document.activeElement === last)) {
      event.preventDefault();
      (event.shiftKey ? last : first).focus();
    }
  }
}

new CookieConsent();
